import Button, { BUTTON_COLOR } from '@ab-core/button';
import { getClassNames } from '@ab-core/functions/styles/classNameFormatter';
import type { InputProps } from '@ab-core/input';
import { BasicInput } from '@ab-core/input';
import { getTestIdProp } from '@ab-core/testing';
import React, { useEffect, useReducer, useState } from 'react';
import type { CounterReducerState } from './reducer';
import { COUNTER_REDUCER_ACTION_KIND, counterReducer } from './reducer';
import { CounterWrapper, Hyphen } from './styled';
import { getReducerStateFromCounter } from './utils';
import { CounterWithTooltip } from './variants';

export type StateUpdateType = CounterReducerState & { lastAction?: string };

export type CounterType = React.ComponentPropsWithoutRef<'div'> & {
    quantity?: number;
    min?: number;
    max?: number;
    step?: number;
    quantityUnit?: string;
    disabled?: boolean;
    stateUpdate?: (state: StateUpdateType) => void;
};

const MAX_INPUT_LENGTH = 6;

const Counter = React.forwardRef<HTMLInputElement, CounterType>((props, ref) => {
    const { disabled, step, min, quantity, max, quantityUnit, className, stateUpdate, ...rest } = props;

    const defaultReducerState = getReducerStateFromCounter({ quantity, min, max, step, quantityUnit });
    const [state, dispatch] = useReducer(counterReducer, defaultReducerState);

    useEffect(() => {
        const newReducerState = getReducerStateFromCounter({ quantity, min, max, step, quantityUnit });
        dispatch({ type: COUNTER_REDUCER_ACTION_KIND.SET_STATE, payload: newReducerState });
    }, [quantity, min, max, step, quantityUnit]);

    const [lastAction, setLastAction] = useState<StateUpdateType['lastAction']>(undefined);

    useEffect(() => {
        if (stateUpdate) {
            stateUpdate({ ...state, lastAction });
        }

        setLastAction(undefined);
    }, [state]);

    const isIncrementDisabled = state.quantity >= state.max;
    const isDecrementDisabled = state.quantity <= state.min;

    const onBlur: InputProps['onBlur'] = () => {
        setLastAction('onBlur');
        dispatch({ type: COUNTER_REDUCER_ACTION_KIND.CHECK_VALUE });
    };
    const onChange: InputProps['onChange'] = (e) => {
        setLastAction('onChange');
        dispatch({ type: COUNTER_REDUCER_ACTION_KIND.SET_VALUE, payload: e.target.value });
    };
    const increment = () => {
        setLastAction('increment');
        dispatch({ type: COUNTER_REDUCER_ACTION_KIND.INCREMENT });
    };
    const decrement = () => {
        setLastAction('decrement');
        dispatch({ type: COUNTER_REDUCER_ACTION_KIND.DECREMENT });
    };
    const onClick: InputProps['onClick'] = () => {
        setLastAction('onClick');
        dispatch({ type: COUNTER_REDUCER_ACTION_KIND.CLEAR });
    };

    const counterWrapperClass = getClassNames({
        prefix: 'counter-wrapper',
        modifier: { disabled },
        className
    });

    return (
        <CounterWrapper {...rest} className={counterWrapperClass}>
            <Button
                {...getTestIdProp('counter-decrease-button')}
                disabled={disabled || isDecrementDisabled}
                onClick={disabled ? undefined : decrement}
                color={BUTTON_COLOR.WHITE}
                iconName="Minus"
            />
            <Hyphen />
            <BasicInput
                {...getTestIdProp('counter-field')}
                ref={ref}
                onBlur={onBlur}
                disabled={disabled}
                value={state.formattedValue}
                onChange={onChange}
                onClick={onClick}
                maxLength={MAX_INPUT_LENGTH}
            />
            <Hyphen />
            <Button
                {...getTestIdProp('counter-increase-button')}
                disabled={disabled || isIncrementDisabled}
                onClick={disabled ? undefined : increment}
                color={BUTTON_COLOR.WHITE}
                iconName="Plus"
            />
        </CounterWrapper>
    );
});

export { Counter, CounterWithTooltip, MAX_INPUT_LENGTH };
